import { Fragment, ReactNode } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'react-i18next';

/**
 * Reusable vertical section navigation. Used by the garage car dashboard, and
 * intended to be reused for other multi-section pages (forum, profile, etc.)
 * with 5+ sections where horizontal tabs stop scaling.
 *
 * Groups are optional — if no groups need labels, pass a single group with no
 * labelKey. Items can carry a badge for unread/pending counters.
 */

export interface SectionNavItem {
  key: string;
  /** Translation key for a fixed label… */
  labelKey?: string;
  /** …or the text itself, for user-named items (e.g. a garage's name). */
  label?: string;
  icon?: ReactNode;
  badge?: number;
}

export interface SectionNavGroup {
  key: string;
  labelKey?: string;
  items: SectionNavItem[];
}

export interface SectionNavProps {
  groups: SectionNavGroup[];
  value: string;
  onChange: (key: string) => void;
  /** Rendered above the list — typically a visual anchor (e.g. car thumbnail). */
  header?: ReactNode;
  /** Rendered below the list — typically owner-scoped actions (edit, delete). */
  footer?: ReactNode;
}

export function SectionNav({ groups, value, onChange, header, footer }: SectionNavProps) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      {header && <Box sx={{ px: 2, py: 2 }}>{header}</Box>}

      <List sx={{ flexGrow: 1, py: 0 }}>
        {groups.map((group, idx) => (
          <Fragment key={group.key}>
            {idx > 0 && <Box sx={{ my: 1 }} />}
            {group.labelKey && (
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ px: 2, display: 'block', lineHeight: 2 }}
              >
                {t(group.labelKey)}
              </Typography>
            )}
            {group.items.map((item) => (
              <ListItem key={item.key} disablePadding sx={{ px: 1 }}>
                <ListItemButton
                  selected={item.key === value}
                  onClick={() => onChange(item.key)}
                  sx={{
                    borderRadius: 1,
                    '&.Mui-selected': {
                      bgcolor: 'transparent',
                      color: 'primary.main',
                      '& .MuiListItemIcon-root': { color: 'primary.main' },
                      '& .MuiListItemText-primary': { fontWeight: 600 },
                      '&:hover': { bgcolor: 'action.hover' },
                    },
                  }}
                >
                  {item.icon && (
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                  )}
                  <ListItemText primary={item.label ?? (item.labelKey ? t(item.labelKey) : '')} />
                  {item.badge !== undefined && item.badge > 0 && (
                    <Chip size="small" color="error" label={item.badge} />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </Fragment>
        ))}
      </List>

      {footer && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ p: 1 }}>{footer}</Box>
        </>
      )}
    </Box>
  );
}
